import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './Context/AuthContext';
import { Separator } from '@/Components/ui/separator';
import { ScrollArea } from '@/Components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/Components/ui/avatar';
import {
  LayoutDashboard, Building2, PlusCircle, Briefcase, FileText,
  ChevronDown, Pencil, Trash2, Users, BarChart3, CreditCard,
  Settings, Tag, UserCog, Sparkles, Eye,
  ChevronLeft, ChevronRight, LogOut, Shield, User, Upload, ScrollText, Lock, Receipt, MessageSquare, Newspaper, KeyRound, SlidersHorizontal, Inbox, Layers
} from 'lucide-react';
import './SideBar.css';

const LOGO_URL = '/admin-logo.png';

const getSectionPath = (section, selectedCompanyId, selectedJobId) => {
  switch (section) {
    case 'super-admin-management': return `/super-admin-management`;
    case 'cheating-features':      return `/cheating-features`;
    case 'services':               return `/services`;
    case 'audit-logs':             return `/audit-logs`;
    case 'whatsapp-conversations': return `/whatsapp-conversations`;
    case 'security':               return `/security`;
    case 'admin-recruiters':       return `/admin-recruiters`;
    case 'blog-manager':           return `/blog-manager`;
    case 'whatsapp-access':        return `/whatsapp-access`;
    case 'feature-access':         return `/feature-access`;
    case 'talent-pool':            return `/talent-pool`;
    case 'plan-requests':          return `/plan-requests`;
    case 'dashboard':              return `/company-admin-home`;
    case 'all-companies':          return `/all-companies`;
    case 'add-company':            return `/add-company-profile`;
    case 'company-profile':        return selectedCompanyId ? `/company-profile?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
    case 'edit-company-profile':   return selectedCompanyId ? `/edit-company-profile?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
    case 'view-jobs':              return selectedCompanyId ? `/all-jobs?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
    case 'create-jd':              return selectedCompanyId ? `/create-JD?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
    case 'upload-jd':              return selectedCompanyId ? `/upload-JD?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
    case 'generate-jd':            return selectedCompanyId ? `/generate-jd?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
    case 'job-description':        return (selectedCompanyId && selectedJobId) ? `/job-description?companyID=${encodeURIComponent(selectedCompanyId)}&jobID=${encodeURIComponent(selectedJobId)}` : (selectedCompanyId ? `/all-jobs?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`);
    case 'view-all-candidates':    return (selectedCompanyId && selectedJobId) ? `/all-candidates?companyID=${encodeURIComponent(selectedCompanyId)}&jobID=${encodeURIComponent(selectedJobId)}` : (selectedCompanyId ? `/all-jobs?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`);
    case 'edit-job':               return (selectedCompanyId && selectedJobId) ? `/edit-job?companyID=${encodeURIComponent(selectedCompanyId)}&jobID=${encodeURIComponent(selectedJobId)}` : (selectedCompanyId ? `/all-jobs?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`);
    case 'view-scheduled-tests':   return (selectedCompanyId && selectedJobId) ? `/view-test?companyID=${encodeURIComponent(selectedCompanyId)}&jobID=${encodeURIComponent(selectedJobId)}` : '/company-admin-home';
    case 'credit-plans':           return `/admin/credits/plans`;
    case 'credit-companies':       return `/admin/credits/companies`;
    case 'credit-usage':           return `/admin/credits/usage`;
    case 'credit-coupons':         return `/admin/credits/coupons`;
    case 'billing':                return `/admin/credits/billing`;
    case 'credit-config':          return `/admin/credits/config`;
    default:                       return `/company-admin-home`;
  }
};

function NavItem({ icon: Icon, label, active, onClick, indent = false, collapsed = false }) {
  return (
    <button
      title={collapsed ? label : undefined}
      onClick={onClick}
      className={`w-full flex items-center rounded-lg text-sm font-medium transition-all duration-150 border-none cursor-pointer text-left
        ${collapsed
          ? 'justify-center p-2.5'
          : indent
            ? 'gap-3 pl-9 pr-3 py-2'
            : 'gap-3 px-3 py-2.5'
        }
        ${active
          ? 'bg-white/15 text-white shadow-sm'
          : 'text-slate-300 hover:bg-white/10 hover:text-white'
        }`}
    >
      {Icon && <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-white' : 'text-slate-400'}`} />}
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
    </button>
  );
}

function SectionLabel({ label, collapsed = false, collapsible = false, isOpen = true, onToggle }) {
  if (collapsed) return <div className="mx-2 my-1.5"><Separator className="bg-white/10" /></div>;
  if (collapsible) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 pt-3 pb-1 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer text-left"
      >
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
        <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform duration-200 ${!isOpen ? '-rotate-90' : ''}`} />
      </button>
    );
  }
  return (
    <div className="px-3 pt-3 pb-1">
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
    </div>
  );
}

export default function SideBar({ activeMain, setActiveMain, selectedCompany, selectedJob, setSelectedCompany, setSelectedJob, onDeleteCompanyClick, onDeleteJobClick, onEnrollCandidateClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout, isSuperAdmin, isAdminAuthenticated, hasPerm } = useAuth();

  const isNormalAdmin = isAdminAuthenticated && !isSuperAdmin;

  const adminEmail  = localStorage.getItem('admin_email') || admin?.email || '';
  const displayName = admin?.full_name || (adminEmail ? adminEmail.split('@')[0] : 'Admin');
  const getInitial  = () => displayName.charAt(0).toUpperCase();

  const jobID     = new URLSearchParams(location.search).get('jobID');
  const companyID = new URLSearchParams(location.search).get('companyID');

  const [showCompanyActions,  setShowCompanyActions]  = useState(false);
  const [showJobActions,      setShowJobActions]      = useState(false);
  const [showPostJobDropdown, setShowPostJobDropdown] = useState(false);
  const [isCollapsed,         setIsCollapsed]         = useState(false);

  // Collapsible sections: closed by default; auto-expand when the current
  // route lives inside one so the highlighted item stays visible.
  const [sectionOpen, setSectionOpen] = useState({
    Companies: false,
    Platform: false,
    Setup: false,
    'Credits & Billing': false,
    Account: false,
  });

  // activeMain → which section it belongs to. Used by the auto-expand effect.
  const SECTION_ROUTES = {
    Companies: ['all-companies', 'view-jobs', 'whatsapp-conversations'],
    Platform: ['admin-recruiters', 'blog-manager', 'audit-logs'],
    Setup: ['super-admin-management', 'add-company', 'services', 'cheating-features', 'whatsapp-access', 'feature-access', 'talent-pool', 'plan-requests'],
    'Credits & Billing': ['credit-companies', 'billing', 'credit-usage', 'credit-plans', 'credit-coupons', 'credit-config'],
    Account: ['security'],
  };

  useEffect(() => {
    // If the current route is inside a collapsed section, open that section
    // so the active item stays highlighted and visible. We never auto-close.
    for (const [name, routes] of Object.entries(SECTION_ROUTES)) {
      if (routes.includes(activeMain)) {
        setSectionOpen(prev => (prev[name] ? prev : { ...prev, [name]: true }));
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMain]);

  const toggleSection = (name) =>
    setSectionOpen(prev => ({ ...prev, [name]: !prev[name] }));

  const postJobDropdownRef = useRef(null);

  useEffect(() => {
    const isCompanyActionActive = companyID || [
      'company-profile', 'edit-company-profile', 'delete-company-profile',
      'view-jobs', 'post-job', 'enroll-candidate', 'view-scheduled-tests',
      'view-all-candidates', 'edit-job', 'delete-job', 'job-description',
      'create-jd', 'upload-jd', 'generate-jd'
    ].includes(activeMain);
    setShowCompanyActions(isCompanyActionActive);

    const isJobActionActive = jobID || [
      'enroll-candidate', 'view-scheduled-tests', 'view-all-candidates',
      'edit-job', 'delete-job', 'job-description'
    ].includes(activeMain);
    setShowJobActions(isJobActionActive);
  }, [activeMain, companyID, jobID]);

  useEffect(() => {
    if (isCollapsed) setShowPostJobDropdown(false);
  }, [isCollapsed]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (postJobDropdownRef.current && !postJobDropdownRef.current.contains(e.target))
        setShowPostJobDropdown(false);
    };
    if (showPostJobDropdown) {
      const t = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 100);
      return () => { clearTimeout(t); document.removeEventListener('mousedown', handleClickOutside); };
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPostJobDropdown]);

  const handleMainClick = (section) => {
    if (section === 'delete-company-profile') {
      if (companyID) onDeleteCompanyClick();
      else { alert('Please select a company to delete first.'); navigate(getSectionPath('all-companies')); }
      return;
    }
    if (section === 'delete-job') {
      if (jobID) onDeleteJobClick();
      else { alert('Please select a job to delete first.'); navigate(getSectionPath('view-jobs', companyID)); }
      return;
    }
    if (section === 'enroll-candidate') {
      if (jobID) onEnrollCandidateClick();
      else { alert('Please select a job to enroll a candidate for first.'); navigate(getSectionPath('view-jobs', companyID)); }
      return;
    }
    if (section === 'post-job-toggle') {
      setShowPostJobDropdown(prev => !prev);
      return;
    }
    navigate(getSectionPath(section, companyID, jobID));
    setShowPostJobDropdown(false);
    if (['all-companies', 'dashboard', 'add-company'].includes(section)) {
      setSelectedCompany(null); setSelectedJob(null);
    } else if (['company-profile', 'view-jobs', 'post-job', 'create-jd', 'upload-jd', 'generate-jd', 'edit-company-profile'].includes(section)) {
      setSelectedJob(null);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <aside
      className={`${isCollapsed ? 'w-16 min-w-[4rem]' : 'w-64 min-w-[16rem]'} bg-[#050F47] flex flex-col h-screen shrink-0 transition-all duration-300 overflow-hidden`}
    >
      {/* ── Top: Logo + collapse toggle ── */}
      <div className={`border-b border-white/10 flex items-center py-3 shrink-0 ${isCollapsed ? 'justify-center px-2' : 'justify-between px-3'}`}>
        {!isCollapsed && (
          <img src={LOGO_URL} alt="Logo" className="h-9 object-contain" />
        )}
        <button
          onClick={() => setIsCollapsed(prev => !prev)}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-white/10 hover:text-white transition-colors shrink-0 border-none cursor-pointer bg-transparent"
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* ── Middle: Nav items ── */}
      {/*
        Reordered from a flat 11-item wall to grouped sections based on actual
        page usage (audit findings):
          - Daily (Dashboard, All Companies, WhatsApp) → top
          - Weekly ops (Recruiters, Blog, Audit Log) → middle
          - Setup / rare (Admin Mgmt, Add Company, Services, Cheating
            Detection, WhatsApp Access) → bottom
          - Personal (Security) → very bottom
        Contextual sections (Company Actions, Credit Management, Job Actions)
        stay where they were — they only appear when their trigger fires.
      */}
      <ScrollArea className="flex-1 px-2 py-2">
        <div className="flex flex-col gap-0.5">
          {/* ── OVERVIEW ── */}
          <NavItem icon={LayoutDashboard} label="Dashboard" active={activeMain === 'dashboard'} onClick={() => handleMainClick('dashboard')} collapsed={isCollapsed} />

          {/* ── COMPANIES ── (core daily work, collapsible) */}
          <div className="mx-1 my-1.5"><Separator className="bg-white/10" /></div>
          <SectionLabel
            label="Companies"
            collapsed={isCollapsed}
            collapsible={!isCollapsed}
            isOpen={sectionOpen.Companies}
            onToggle={() => toggleSection('Companies')}
          />
          {(isCollapsed || sectionOpen.Companies) && (
            <>
              {!isNormalAdmin && (
                <>
                  <NavItem icon={Building2} label="All Companies" active={activeMain === 'all-companies' || showCompanyActions} onClick={() => handleMainClick('all-companies')} collapsed={isCollapsed} />
                  {!!isSuperAdmin && (
                    <NavItem icon={MessageSquare} label="WhatsApp" active={activeMain === 'whatsapp-conversations'} onClick={() => handleMainClick('whatsapp-conversations')} collapsed={isCollapsed} />
                  )}
                </>
              )}
              {!!isNormalAdmin && (
                <NavItem icon={Briefcase} label="My Assigned Jobs" active={activeMain === 'view-jobs' || showJobActions} onClick={() => handleMainClick('all-companies')} collapsed={isCollapsed} />
              )}
            </>
          )}

          {/* ── Company Actions (contextual — inline after All Companies so it's discoverable) ── */}
          {!!(showCompanyActions && !isNormalAdmin) && (
            <>
              <div className="mx-1 my-1.5"><Separator className="bg-white/10" /></div>
              <SectionLabel label="Company Actions" collapsed={isCollapsed} />
              <NavItem icon={Building2} label="Company Profile" active={activeMain === 'company-profile'} onClick={() => handleMainClick('company-profile')} collapsed={isCollapsed} />
              <NavItem icon={Eye} label="View Posted Jobs" active={activeMain === 'view-jobs' || showJobActions} onClick={() => handleMainClick('view-jobs')} collapsed={isCollapsed} />

              {isCollapsed ? (
                <NavItem icon={FileText} label="Post Job" active={['post-job','create-jd','upload-jd','generate-jd','after-jd'].includes(activeMain)} onClick={() => handleMainClick('create-jd')} collapsed={true} />
              ) : (
                <div className="flex flex-col gap-0.5" ref={postJobDropdownRef}>
                  <button
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 border-none cursor-pointer text-left
                      ${['post-job','create-jd','upload-jd','generate-jd','after-jd'].includes(activeMain) ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'}`}
                    onClick={(e) => { e.stopPropagation(); handleMainClick('post-job-toggle'); }}
                  >
                    <FileText className="w-4 h-4 shrink-0 text-slate-400" />
                    <span className="flex-1">Post Job</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${showPostJobDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`flex flex-col gap-0.5 overflow-hidden transition-all duration-300 ${showPostJobDropdown ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <NavItem icon={FileText} label="Create JD" active={activeMain === 'create-jd'} onClick={() => handleMainClick('create-jd')} indent />
                    <NavItem icon={Sparkles} label="Generate JD with AI" active={activeMain === 'generate-jd'} onClick={() => handleMainClick('generate-jd')} indent />
                    <NavItem icon={Upload} label="Upload JD" active={activeMain === 'upload-jd'} onClick={() => handleMainClick('upload-jd')} indent />
                  </div>
                </div>
              )}

              <NavItem icon={Pencil} label="Edit Company Profile" active={activeMain === 'edit-company-profile'} onClick={() => handleMainClick('edit-company-profile')} collapsed={isCollapsed} />
              <NavItem icon={Trash2} label="Delete Company" active={activeMain === 'delete-company-profile'} onClick={() => handleMainClick('delete-company-profile')} collapsed={isCollapsed} />
            </>
          )}

          {/* ── Job Actions (contextual — moved up so it's inline after Company Actions when both apply) ── */}
          {!!showJobActions && (
            <>
              <div className="mx-1 my-1.5"><Separator className="bg-white/10" /></div>
              <SectionLabel label="Job Actions" collapsed={isCollapsed} />
              <NavItem icon={Users} label="Enroll Candidate"    active={activeMain === 'enroll-candidate'}    onClick={() => handleMainClick('enroll-candidate')}    collapsed={isCollapsed} />
              <NavItem icon={Users} label="View All Candidates" active={activeMain === 'view-all-candidates'} onClick={() => handleMainClick('view-all-candidates')} collapsed={isCollapsed} />
              {!isNormalAdmin && (
                <NavItem icon={Trash2} label="Delete Job" active={activeMain === 'delete-job'} onClick={() => handleMainClick('delete-job')} collapsed={isCollapsed} />
              )}
            </>
          )}

          {/* ── PLATFORM ── (weekly ops — super admin only, collapsible) */}
          {!!isSuperAdmin && (
            <>
              <div className="mx-1 my-1.5"><Separator className="bg-white/10" /></div>
              <SectionLabel
                label="Platform"
                collapsed={isCollapsed}
                collapsible={!isCollapsed}
                isOpen={sectionOpen.Platform}
                onToggle={() => toggleSection('Platform')}
              />
              {(isCollapsed || sectionOpen.Platform) && (
                <>
                  <NavItem icon={Users} label="Recruiters" active={activeMain === 'admin-recruiters'} onClick={() => handleMainClick('admin-recruiters')} collapsed={isCollapsed} />
                  <NavItem icon={Newspaper} label="Blog" active={activeMain === 'blog-manager'} onClick={() => handleMainClick('blog-manager')} collapsed={isCollapsed} />
                  <NavItem icon={ScrollText} label="Audit Log" active={activeMain === 'audit-logs'} onClick={() => handleMainClick('audit-logs')} collapsed={isCollapsed} />
                </>
              )}
            </>
          )}

          {/* ── SETUP ── (rare / one-time config — super admin only, collapsible) */}
          {!!isSuperAdmin && (
            <>
              <div className="mx-1 my-1.5"><Separator className="bg-white/10" /></div>
              <SectionLabel
                label="Setup"
                collapsed={isCollapsed}
                collapsible={!isCollapsed}
                isOpen={sectionOpen.Setup}
                onToggle={() => toggleSection('Setup')}
              />
              {(isCollapsed || sectionOpen.Setup) && (
                <>
                  <NavItem icon={UserCog} label="Admin Management" active={activeMain === 'super-admin-management'} onClick={() => handleMainClick('super-admin-management')} collapsed={isCollapsed} />
                  <NavItem icon={PlusCircle} label="Add Company" active={activeMain === 'add-company'} onClick={() => handleMainClick('add-company')} collapsed={isCollapsed} />
                  <NavItem icon={Settings} label="Services" active={activeMain === 'services'} onClick={() => handleMainClick('services')} collapsed={isCollapsed} />
                  <NavItem icon={Shield} label="Cheating Detection" active={activeMain === 'cheating-features'} onClick={() => handleMainClick('cheating-features')} collapsed={isCollapsed} />
                  <NavItem icon={KeyRound} label="WhatsApp Access" active={activeMain === 'whatsapp-access'} onClick={() => handleMainClick('whatsapp-access')} collapsed={isCollapsed} />
                  <NavItem icon={SlidersHorizontal} label="Feature Access" active={activeMain === 'feature-access'} onClick={() => handleMainClick('feature-access')} collapsed={isCollapsed} />
                  <NavItem icon={Layers} label="Talent Pool" active={activeMain === 'talent-pool'} onClick={() => handleMainClick('talent-pool')} collapsed={isCollapsed} />
                  <NavItem icon={Inbox} label="Plan Requests" active={activeMain === 'plan-requests'} onClick={() => handleMainClick('plan-requests')} collapsed={isCollapsed} />
                </>
              )}
            </>
          )}

          {/* ── CREDITS & BILLING ── (gated by credits.read perm, collapsible) */}
          {!!hasPerm('credits.read') && (
            <>
              <div className="mx-1 my-1.5"><Separator className="bg-white/10" /></div>
              <SectionLabel
                label="Credits & Billing"
                collapsed={isCollapsed}
                collapsible={!isCollapsed}
                isOpen={sectionOpen['Credits & Billing']}
                onToggle={() => toggleSection('Credits & Billing')}
              />
              {(isCollapsed || sectionOpen['Credits & Billing']) && (
                <>
                  {/* Reordered by frequency within the group: weekly-used items first, setup-only last */}
                  <NavItem icon={Building2}   label="Company Credits"  active={activeMain === 'credit-companies'} onClick={() => handleMainClick('credit-companies')} collapsed={isCollapsed} />
                  <NavItem icon={Receipt}     label="Billing"          active={activeMain === 'billing'}          onClick={() => handleMainClick('billing')}          collapsed={isCollapsed} />
                  <NavItem icon={BarChart3}   label="Credit Usage"     active={activeMain === 'credit-usage'}     onClick={() => handleMainClick('credit-usage')}     collapsed={isCollapsed} />
                  <NavItem icon={CreditCard}  label="Credit Plans"     active={activeMain === 'credit-plans'}     onClick={() => handleMainClick('credit-plans')}     collapsed={isCollapsed} />
                  <NavItem icon={Tag}         label="Coupons"          active={activeMain === 'credit-coupons'}   onClick={() => handleMainClick('credit-coupons')}   collapsed={isCollapsed} />
                  <NavItem icon={Settings}    label="Credit Config"    active={activeMain === 'credit-config'}    onClick={() => handleMainClick('credit-config')}    collapsed={isCollapsed} />
                </>
              )}
            </>
          )}

          {/* ── ACCOUNT ── (personal — used rarely, collapsible) */}
          <div className="mx-1 my-1.5"><Separator className="bg-white/10" /></div>
          <SectionLabel
            label="Account"
            collapsed={isCollapsed}
            collapsible={!isCollapsed}
            isOpen={sectionOpen.Account}
            onToggle={() => toggleSection('Account')}
          />
          {(isCollapsed || sectionOpen.Account) && (
            <NavItem icon={Lock} label="Security" active={activeMain === 'security'} onClick={() => handleMainClick('security')} collapsed={isCollapsed} />
          )}
        </div>
      </ScrollArea>

      {/* ── Bottom: Profile + Logout ── */}
      <div className="border-t border-white/10 p-2 shrink-0">
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-1">
            <button
              title={displayName}
              className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white/10 transition-colors border-none cursor-pointer bg-transparent"
            >
              <Avatar className="w-7 h-7">
                <AvatarFallback className="bg-[#142DB7] text-white text-xs font-bold">{getInitial()}</AvatarFallback>
              </Avatar>
            </button>
            <button
              title="Sign Out"
              onClick={handleLogout}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors border-none cursor-pointer bg-transparent"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="rounded-xl bg-white/5 p-3">
            <div className="flex items-center gap-2.5 mb-3">
              <Avatar className="w-9 h-9 shrink-0">
                <AvatarFallback className="bg-[#142DB7] text-white text-sm font-bold">{getInitial()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">{displayName}</div>
                <div className="flex items-center gap-1 mt-0.5">
                  {isSuperAdmin
                    ? <Shield className="w-3 h-3 text-yellow-300" />
                    : <User className="w-3 h-3 text-blue-300" />
                  }
                  <span className="text-xs text-slate-400">{isSuperAdmin ? 'Super Admin' : 'Admin'}</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors border-none cursor-pointer bg-transparent"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
